import { useState, useCallback } from 'react'

type IAnyFunction = (...args: any[]) => any

/**
 * A hook that provides loading state management for any function.
 * The wrapped function will automatically manage a loading state while executing,
 * regardless of whether it's async or sync.
 *
 * @returns {Object} An object containing:
 * - isLoading: boolean - Current loading state
 * - withLoading: function - Wrapper function that manages the loading state
 */
export function useWithLoading() {
    const [isLoading, setIsLoading] = useState(false)

    const withLoading = useCallback(<T extends IAnyFunction>(fn: T) => {
        return async (...args: Parameters<T>) => {
            try {
                setIsLoading(true)

                const result = await fn(...args)

                return result
            } finally {
                setIsLoading(false)
            }
        }
    }, [])

    return {
        isLoading,
        withLoading,
    }
}
